import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { EnhancedError } from '../../../components/_error/EnhancedError';
import { Layout } from '../../../components/layout/Layout';
import { PhotoGrid } from '../../../components/photo-grid/PhotoGrid';
import { ScrollToTop } from '../../../components/scroll-to-top/ScrollToTop';
import { PdpProvider, usePDP } from '../../../contexts/pdp/usePDP';
import { ProductsAddedToBagDrawer } from '../../../contexts/products-added-to-bag-drawer/ProductsAddedToBagDrawer';
import { Breadcrumb } from '../../../ui-components';
import { useBreadcrumbs } from '../../../ui-components/breadcrumbs/useBreadcrumbs';
import { canonicalUrlCleanup } from '../../../utils/canonical-url-cleanup';
import { BreadcrumbSchema } from '../../../utils/schema/BreadcrumbSchema';
import { FAQSchema } from '../../../utils/schema/FAQSchema';
import { ProductSchema } from '../../../utils/schema/ProductSchema';
import { isServer } from '../../../utils/utils';
import { getCanonicalUrlForGC } from '../../utils/canonical-url';
import { configHtmlCache } from '../../utils/config-html-cache';
import { GiftCardsFAQSection } from '../components/GiftCardsFAQSection';
import { CheckBalanceModal } from '../components/check-balance-modal';
import { convertFAQItemsToSchemaItems, getFAQItems } from '../utils/faq';
import { DiscoverCookingClass } from './components/DiscoverCookingClass';
import {
    GDPBreadcrumbsSkeleton,
    GDPBuyBoxSkeleton,
    GDPFAQSkeleton,
    GDPPhotoGridSkeleton,
    GDPWhatCanYouGetSkeleton,
} from './components/GDPSkeleton';
import { GiftCardBuyBox } from './components/GiftCardBuyBox';
import { WhatCanYouGetSection } from './components/WhatCanYouGetSection';
import { useGiftCardLocation } from './hooks/useGiftCardLocation';

export const GiftCardDetailPageLayout = (): ReactElement => {
    const headerRef = useRef<HTMLDivElement>(null);
    const [isCheckBalanceModalOpen, setIsCheckBalanceModalOpen] = useState(false);
    const { product, isLoading } = usePDP();
    const breadcrumbs = useBreadcrumbs(product?.c_primaryCategory?.parentCategoryTree);
    const intl = useIntl();
    const faqSectionRef = useRef<HTMLDivElement>(null);
    const { initializeDefaultQueryParams, initializeDefaultUsePDPState } = useGiftCardLocation();
    const canonicalUrl = getCanonicalUrlForGC({
        c_canonicalProductId: product?.c_canonicalProductId,
        c_gcType: product?.c_productType,
    });

    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        initializeDefaultQueryParams();
        initializeDefaultUsePDPState();
        if (product?.c_pwaUrl && location.pathname !== product.c_pwaUrl) {
            history.replace(product.c_pwaUrl + location.search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExploreFAQsClick = (): void => {
        if (faqSectionRef.current) {
            const headerOffset = headerRef.current?.offsetHeight ?? 0;
            const elementPosition = faqSectionRef.current?.offsetTop ?? 0;
            const gap = 16;
            const offsetPosition = elementPosition - (headerOffset + gap);

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    const handleCheckBalanceClick = (): void => {
        setIsCheckBalanceModalOpen(!isCheckBalanceModalOpen);
    };

    const faqItems = getFAQItems(intl, handleCheckBalanceClick);
    const faqSchemaItems = convertFAQItemsToSchemaItems(faqItems);

    return (
        <Layout headerRef={headerRef} className="bg-slt-grey-150">
            <Helmet>
                {/* canonical link with reference to master product id */}
                <link rel="canonical" href={canonicalUrlCleanup(canonicalUrl)} />
            </Helmet>
            <ProductSchema product={product} />
            <FAQSchema faqItems={faqSchemaItems} />
            <CheckBalanceModal
                id="check-balance-modal"
                isOpen={isCheckBalanceModalOpen}
                onClose={handleCheckBalanceClick}
            />
            <Flex direction="column" background="white">
                <GDPBreadcrumbsSkeleton isLoaded={!isLoading}>
                    <Breadcrumb itemsList={breadcrumbs} />
                    <BreadcrumbSchema breadcrumbTrail={breadcrumbs} />
                </GDPBreadcrumbsSkeleton>
                <Flex
                    gap={{ base: 16 }}
                    direction="column"
                    paddingX={{ md: 8, lg: 16 }}
                    paddingBottom={16}
                >
                    <Flex gap={8}>
                        <Box display={{ base: 'none', md: 'block' }} width={{ md: '60%' }}>
                            <GDPPhotoGridSkeleton isLoaded={!isLoading}>
                                <PhotoGrid productId={product?.id || ''} thumbnailDisabled />
                            </GDPPhotoGridSkeleton>
                        </Box>
                        <Box flex="1">
                            <GDPBuyBoxSkeleton isLoaded={!isLoading}>
                                {!isLoading && (
                                    <GiftCardBuyBox
                                        onExploreFAQsClick={handleExploreFAQsClick}
                                        onCheckBalanceClick={handleCheckBalanceClick}
                                    />
                                )}
                            </GDPBuyBoxSkeleton>
                        </Box>
                    </Flex>
                    <GDPWhatCanYouGetSkeleton isLoaded={!isLoading}>
                        <WhatCanYouGetSection />
                    </GDPWhatCanYouGetSkeleton>
                    <DiscoverCookingClass />
                    <GDPFAQSkeleton isLoaded={!isLoading}>
                        <GiftCardsFAQSection
                            onCheckBalanceClick={handleCheckBalanceClick}
                            faqSectionRef={faqSectionRef}
                        />
                    </GDPFAQSkeleton>
                </Flex>
            </Flex>
            <ProductsAddedToBagDrawer />
            <ScrollToTop />
        </Layout>
    );
};

const GiftCardDetailPage: PageFC = () => {
    const { sku } = useParams<{
        sku: string;
    }>();

    return (
        <ErrorBoundary FallbackComponent={EnhancedError}>
            <PdpProvider productId={sku}>
                <GiftCardDetailPageLayout />
            </PdpProvider>
        </ErrorBoundary>
    );
};

GiftCardDetailPage.getProps = ({ res }): Promise<object> => {
    configHtmlCache(res);
    return Promise.resolve({});
};

GiftCardDetailPage.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

export { GiftCardDetailPage };
